import * as React from 'react';

import { ContextualMenu, IContextualMenuItem, IContextualMenuProps, IFocusZone, KeyCodes } from '@fluentui/react';

import { UILocation } from '@1js/acui-common';
import { withMeasuredContext } from '@1js/acui-resize-group';
import { withShadowRendering } from '@1js/acui-shadow-rendering';

type AppContextualMenuProps = IContextualMenuProps & {
  isShadowRendering?: boolean;
  isMeasured?: boolean;
};

interface AppContextualMenuState {
  focusInRightClickMenu?: boolean;
}

/**
 * This components is the customized version of the Fabric contextual menu.
 * Currently, it does not do much customization, but we will add more logic as our
 * shadow rendering gets more sophisticated.
 */
export class AppContextualMenuBase extends React.PureComponent<AppContextualMenuProps, AppContextualMenuState> {
  private focusZoneRef = React.createRef<IFocusZone>();
  private shouldRestoreFocus: boolean = false;

  constructor(props: AppContextualMenuProps) {
    super(props);

    this.state = {
      focusInRightClickMenu: false,
    };
  }

  public render() {
    const { isMeasured, focusZoneProps, calloutProps } = this.props;
    const supportsRightClickMenu = this.supportsRightClickMenu();
    return isMeasured ? null : (
      <ContextualMenu
        {...this.props}
        onMenuOpened={this.onMenuOpened}
        focusZoneProps={{ ...focusZoneProps, componentRef: this.focusZoneRef }}
        onRestoreFocus={this.restoreFocus}
        onDismiss={this.onDismiss}
        delayUpdateFocusOnHover={supportsRightClickMenu ? this.state.focusInRightClickMenu : undefined}
        shouldFocusOnMount={supportsRightClickMenu ? !this.state.focusInRightClickMenu : undefined}
        calloutProps={{
          preventDismissOnEvent: supportsRightClickMenu ? this.preventDismissOnEvent : undefined,
          ...calloutProps,
        }}
      />
    );
  }

  private restoreFocus = (options: { containsFocus: boolean; originalElement?: HTMLElement | Window | undefined }) => {
    /**
     * In most cases in the function, we don't actually want to do anything and we want
     * to allow focus to move around as it normally does. This ensures that modal dialogs
     * opened via a menu command properly get focus. However, when we use escape to dismiss
     * the menu, we want to ensure focus goes to the original element aka the element hosting
     * the menu. This makes it easy to keyboard users to open and close menus and keep focus
     * on the same element.
     */

    // Check if we should be restoring focus, if the menu has focus (containsFocus) and we haven't
    // clicked outside, and if the originalElement is valid before moving focus.
    if (this.shouldRestoreFocus && options && options.containsFocus && options.originalElement) {
      options.originalElement.focus();
      this.shouldRestoreFocus = false;
    }
  };

  private onDismiss = (ev?: React.MouseEvent | React.KeyboardEvent, dismissAll?: boolean) => {
    const { onDismiss } = this.props;

    // Check if we are being dismissed via a dismiss menu key event and set the
    // bool to restore focus.
    const keyboardEvent: React.KeyboardEvent<HTMLElement> = ev as React.KeyboardEvent<HTMLElement>;
    if (
      keyboardEvent &&
      (keyboardEvent.keyCode === KeyCodes.escape ||
        (keyboardEvent.altKey && keyboardEvent.keyCode === KeyCodes.up) ||
        keyboardEvent.keyCode === KeyCodes.alt)
    ) {
      this.shouldRestoreFocus = true;
    }

    if (onDismiss) {
      onDismiss(ev, dismissAll);
    }

    if (this.state.focusInRightClickMenu) {
      this.setState({ focusInRightClickMenu: false });
    }
  };

  /**
   * Ensure that tab will move focus to the first menuitem button,
   * if focus is initially on the container.
   */
  private onMenuOpened = (contextualMenu?: IContextualMenuProps) => {
    if (this.props.onMenuOpened) {
      this.props.onMenuOpened(contextualMenu);
    }

    // Reset focus on first menuitem
    if (!this.props.shouldFocusOnContainer && this.focusZoneRef && this.focusZoneRef.current) {
      this.focusZoneRef.current.focusElement(this.getFirstMenuItem());
    }
  };

  private getFirstMenuItem = (): HTMLElement | undefined => {
    const menuId = this.props.id ?? (this.props.calloutProps && this.props.calloutProps.id);
    if (menuId) {
      const menuElement = document.getElementById(menuId);
      if (menuElement) {
        const firstMenuItem = menuElement.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (firstMenuItem) {
          return firstMenuItem as HTMLElement;
        }
      }
    }

    return undefined;
  };

  /**
   * Prevent dismissing the contextual menu when focusing onto a context menu when the contextual menu supports context menus
   */
  private preventDismissOnEvent = (ev: Event | React.FocusEvent | React.KeyboardEvent | React.MouseEvent) => {
    const focusEvent = ev as React.FocusEvent;
    // Don't check if we should prevent dismiss if it isn't a focus event or we don't have a related target (aka mouse click dismissal)
    if (!focusEvent) {
      return false;
    }

    let target = ev.target as HTMLElement;

    const focusInRightClickMenu = !!(
      target?.getAttribute('data-unique-id')?.startsWith(UILocation.ContextMenu) ||
      target.id.startsWith(UILocation.ContextMenu)
    );
    this.setState({ focusInRightClickMenu });

    return focusInRightClickMenu;
  };

  // Return whether or not an item has a rightclick menu
  private supportsRightClickMenu = (): boolean => {
    return this.props.items?.some(
      (section: IContextualMenuItem) =>
        section.data?.contextMenuMap ||
        section.sectionProps?.items.some((menuItem: IContextualMenuItem) => menuItem.data?.contextMenuMap !== undefined)
    );
  };
}

export const AppContextualMenu = withMeasuredContext(withShadowRendering(AppContextualMenuBase));
